// ShareCardPreview — a live, editorial Open Graph card preview the affiliate
// can show their audience exactly what surfaces when they share their link.
// Pure CSS/SVG; no remote rendering.
import { C } from "@/lib/brand";
import Logo from "@/components/Logo";

export default function ShareCardPreview({ url, title, description, accent = "peach", testId = "share-card-preview" }) {
  const accents = {
    peach: { bg: C.ink, text: C.cream, accent: C.peach, deep: C.peachDeep },
    sage: { bg: C.ink, text: C.cream, accent: C.sage, deep: C.sageDark },
    lavender: { bg: C.ink, text: C.cream, accent: C.lavender, deep: "#5b3f8c" },
    ochre: { bg: C.ink, text: C.cream, accent: C.ochre, deep: C.ochreDark },
  };
  const a = accents[accent] || accents.peach;
  const safeUrl = (url || "voya9.com").replace(/^https?:\/\//, "");

  return (
    <div data-testid={testId} style={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      {/* The OG image preview itself — 1.91:1 aspect ratio (Open Graph standard) */}
      <div
        style={{
          aspectRatio: "1.91 / 1",
          background: a.bg,
          color: a.text,
          padding: 24,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: 200,
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        {/* Decorative orb */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: a.accent,
            opacity: 0.12,
            filter: "blur(2px)",
          }}
        />
        <div style={{ position: "relative", zIndex: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <Logo light size={18} />
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.25em", color: a.accent }}>
            ★ INVITE-ONLY
          </span>
        </div>
        <div style={{ position: "relative", zIndex: 2, minWidth: 0 }}>
          <div
            style={{
              fontSize: "clamp(20px, 4vw, 28px)",
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              textTransform: "uppercase",
              color: a.text,
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {title || "Feel like you again."}
          </div>
          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              lineHeight: 1.5,
              color: "rgba(250,246,240,0.78)",
              maxWidth: "92%",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "break-word",
            }}
          >
            {description || "VOYA9 — a private, physician-supervised wellness platform. Personalized programs for the goals that actually matter."}
          </div>
        </div>
      </div>

      {/* The metadata strip a social platform renders below the OG image */}
      <div
        style={{
          background: C.bone,
          padding: "12px 14px",
          borderTop: `1px solid ${C.warmGray3}`,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: C.warmGray,
            textTransform: "uppercase",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {safeUrl}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            fontWeight: 700,
            color: C.ink,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title || "Feel like you again. — VOYA9"}
        </div>
      </div>
    </div>
  );
}
